<template>
  <div class="staff-subscription-plan-form" data-testid="staff-subscription-plan-form">
    <form @submit.prevent="handleSubmit">
      <div class="form-fields">
        <input-text
          v-model="formData.name"
          elem-id="staff-subscription-plan-name"
          :label="t('Nome')"
          data-testid="staff-subscription-plan-name"
          class="field-full"
        />
        <input-number
          v-model="formData.monthly_credit_grant"
          elem-id="staff-subscription-plan-monthly-credit-grant"
          :label="t('Créditos mensais')"
          data-testid="staff-subscription-plan-monthly-credit-grant"
        />
        <select-list
          v-model="formData.cycle"
          elem-id="staff-subscription-plan-cycle"
          :label="t('Ciclo')"
          :itens-select="cycleOptions"
          data-testid="staff-subscription-plan-cycle"
        />
        <input-toggle
          v-model="activeBool"
          elem-id="staff-subscription-plan-active"
          :label="t('Ativo')"
          data-testid="staff-subscription-plan-active"
        />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { useSubscriptionAdmin } from '../../../composables/useSubscriptionAdmin'
import { MICRO_PER_CREDIT } from '../../../composables/useCreditWallet'
import { log } from '../../../utils/DebugLogger'
import type { PlanGroup } from '../../../composables/useSubscriptionAdmin'

const { t } = useI18n()
const { createPlan, savePlanFields } = useSubscriptionAdmin()

const props = defineProps<{
  plan?: PlanGroup | null
}>()

const emit = defineEmits<{
  (e: 'saved', payload: { id: number; created: boolean }): void
  (e: 'validation-failed'): void
}>()

/** true when no plan is provided — we are creating a new plan. */
const isCreateMode = computed(() => props.plan == null)

/**
 * UNIT BOUNDARY — the form field is "Créditos mensais" and holds CREDITS, while
 * subscription_plans.monthly_credit_grant is stored (and granted) in MICRO-credits,
 * exactly like ai_credit_packs.credits. Same convention as StaffCreditsTopupForm
 * and ManualAdjustmentForm: convert at the page layer, never send the raw number.
 *
 * Without this a staff user typing 50 created a plan granting 50 µcr = 0.00005
 * credits — the wallet read as empty and the subscription looked like it had
 * granted nothing at all (franchise 14, 2026-08-11).
 */
interface PlanFormData {
  name: string
  /** CREDITS as typed by the user — converted to µcr on submit. */
  monthly_credit_grant: number | null
  cycle: string
  active: number
}

const formData = reactive<PlanFormData>({
  name: '',
  monthly_credit_grant: null,
  cycle: 'monthly',
  active: 1,
})

const isSaving = ref(false)

const cycleOptions = computed(() => [
  { ID: 'monthly', Name: t('Mensal') },
  { ID: 'yearly', Name: t('Anual') },
])

// Map active integer ↔ boolean for InputToggle
const activeBool = computed({
  get: () => formData.active === 1,
  set: (v: boolean) => { formData.active = v ? 1 : 0 },
})

function populateFromPlan(plan: PlanGroup): void {
  formData.name = plan.name
  // µcr (stored) → credits (displayed)
  formData.monthly_credit_grant = plan.monthly_credit_grant / MICRO_PER_CREDIT
  formData.cycle = plan.cycle
  formData.active = plan.active
}

function resetForm(): void {
  formData.name = ''
  formData.monthly_credit_grant = null
  formData.cycle = 'monthly'
  formData.active = 1
}

function validateForm(): boolean {
  if (!formData.name.trim()) {
    eventBus.emit('globalMessage.error', t('Nome é obrigatório'))
    emit('validation-failed')
    return false
  }
  if (formData.monthly_credit_grant === null) {
    eventBus.emit('globalMessage.error', t('Créditos mensais são obrigatórios'))
    emit('validation-failed')
    return false
  }
  return true
}

async function handleSubmit(): Promise<void> {
  if (!validateForm()) return
  // credits (typed) → µcr (stored/granted). See PlanFormData.
  const grantMicro = Math.round(formData.monthly_credit_grant! * MICRO_PER_CREDIT)
  try {
    isSaving.value = true
    if (isCreateMode.value) {
      const newId = await createPlan({
        name: formData.name,
        cycle: formData.cycle,
        monthly_credit_grant: grantMicro,
        active: formData.active,
      })
      log('StaffSubscriptionPlanForm', 'handleSubmit', 'Plano criado', { id: newId })
      eventBus.emit('globalMessage.success', t('Plano salvo com sucesso'))
      emit('saved', { id: newId, created: true })
    } else {
      // D1: the backend may VERSION the plan (grant/cycle change) and respond
      // with the NEW plan id — emit that effective id, never the stale one.
      const effectiveId = await savePlanFields(props.plan!.id, {
        name: formData.name,
        cycle: formData.cycle,
        monthly_credit_grant: grantMicro,
        active: formData.active,
      })
      log('StaffSubscriptionPlanForm', 'handleSubmit', 'Plano salvo', { id: props.plan?.id, effectiveId })
      eventBus.emit('globalMessage.success', t('Plano salvo com sucesso'))
      emit('saved', { id: effectiveId, created: false })
    }
  } catch (err) {
    log('StaffSubscriptionPlanForm', 'handleSubmit', 'Erro ao salvar plano', err)
    eventBus.emit('globalMessage.error', t('Erro ao salvar plano'))
  } finally {
    isSaving.value = false
  }
}

// Expose for parent Modal to trigger submit and track state
defineExpose({
  handleSubmit,
  isSaving,
  resetForm,
  populateFromPlan,
})

watch(
  () => props.plan,
  (plan) => {
    if (plan) {
      populateFromPlan(plan)
    } else {
      resetForm()
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.form-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
}

.field-full {
  flex: 1 1 100%;
}
</style>
